/** CloudFront WAF blocks email HTML (`style=`) and bodies over about 8KB. */
const WAF_MAX_BODY_BYTES = 7500;
const WAF_HTML_RE = /style\s*=|on\w+\s*=|<script|<iframe|javascript:/i;

async function gzipToBase64(value: string): Promise<string> {
	const stream = new Blob([value]).stream().pipeThrough(new CompressionStream("gzip"));
	const buffer = await new Response(stream).arrayBuffer();
	const bytes = new Uint8Array(buffer);
	let binary = "";
	for (let i = 0; i < bytes.length; i++) {
		binary += String.fromCharCode(bytes[i]!);
	}
	return btoa(binary);
}

function jsonBytes(value: unknown): number {
	return new TextEncoder().encode(JSON.stringify(value)).length;
}

/** Pack HTML so the production firewall does not drop the save. */
export async function packEmailHtml(value: string): Promise<string> {
	if (!WAF_HTML_RE.test(value) && jsonBytes({ v: value }) < WAF_MAX_BODY_BYTES) {
		return value;
	}
	return `gz:${await gzipToBase64(value)}`;
}

export async function packSequenceStep(step: SequenceStep): Promise<SequenceStep> {
	return {
		...step,
		body: await packEmailHtml(step.body),
	};
}

type SequenceStep = {
	subject: string;
	body: string;
	delayDays: number;
	includePromo?: boolean;
	promo?: unknown;
};

type SequenceUpsert = {
	name: string;
	keyword: string;
	active?: boolean;
	steps: SequenceStep[];
	sendWindows?: { weekday: number; time: string }[];
	defaultPromo?: unknown;
};

/** Pack sequence email HTML before PATCH/POST. */
export async function packSequenceUpsert<T extends SequenceUpsert>(
	body: T,
): Promise<T & { stepsPacked?: string }> {
	const needsPack =
		jsonBytes(body) >= WAF_MAX_BODY_BYTES ||
		body.steps.some((step) => WAF_HTML_RE.test(step.body));
	if (!needsPack) return body;

	const packedSteps = await Promise.all(
		body.steps.map(async (step) => ({
			...step,
			body: await packEmailHtml(step.body),
		})),
	);
	const withPackedSteps = { ...body, steps: packedSteps };
	if (jsonBytes(withPackedSteps) < WAF_MAX_BODY_BYTES) return withPackedSteps;

	const { steps: _steps, ...rest } = body;
	return {
		...rest,
		steps: [],
		stepsPacked: await packEmailHtml(JSON.stringify(body.steps)),
	} as unknown as T & { stepsPacked?: string };
}
